/**
 * 模态框状态管理 Hook
 */

import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ANIMATION_DURATION } from '../constants/ui';
import { useLatestRef } from './useLatestRef';

/**
 * useModal 返回值
 */
export interface UseModalReturn<T = unknown> {
  /** 模态框是否可见 */
  visible: boolean;
  /** 模态框携带的数据 */
  data: T | null;
  /** 加载中状态 */
  loading: boolean;
  /** 打开模态框 (可传入数据) */
  open: (data?: T | undefined) => void;
  /** 关闭模态框 */
  close: () => void;
  /** 切换模态框可见状态 */
  toggle: () => void;
  /** 设置加载状态 */
  setLoading: (loading: boolean) => void;
  /** 设置数据 */
  setData: Dispatch<SetStateAction<T | null>>;
}

/**
 * 模态框状态管理 Hook
 *
 * @example
 * ```tsx
 * const editModal = useModal<User>();
 *
 * <Button onClick={() => editModal.open(record)}>编辑</Button>
 *
 * <Modal
 *   open={editModal.visible}
 *   onCancel={editModal.close}
 *   confirmLoading={editModal.loading}
 * >
 *   <UserForm initialValues={editModal.data} />
 * </Modal>
 * ```
 */
export function useModal<T = unknown>(): UseModalReturn<T> {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // 组件卸载时清理 timer
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const open = useCallback((modalData?: T | undefined) => {
    clearTimeout(timerRef.current);
    setData(modalData ?? null);
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    setLoading(false);
    // 延迟清空数据，避免关闭动画中数据闪烁
    timerRef.current = setTimeout(
      () => setData(null),
      ANIMATION_DURATION.NORMAL,
    );
  }, []);

  // 用 useLatestRef 追踪当前 visible 状态，使 toggle 无需依赖闭包中的 visible
  const visibleRef = useLatestRef(visible);

  const toggle = useCallback(() => {
    if (visibleRef.current) {
      setVisible(false);
      setLoading(false);
      timerRef.current = setTimeout(
        () => setData(null),
        ANIMATION_DURATION.NORMAL,
      );
    } else {
      clearTimeout(timerRef.current);
      setVisible(true);
    }
  }, [visibleRef]);

  return {
    visible,
    data,
    loading,
    open,
    close,
    toggle,
    setLoading,
    setData,
  };
}
